/**
 * Deterministic per-room color mapping for the week-view chips.
 *
 * Indexing is by the room's position in the building's `bookable` rooms list.
 * Palette has 10 colors; rooms past index 9 cycle (rare given the Phase 1
 * fan-out cap of 10 rooms per request).
 *
 * Colors are theme-aware CSS custom properties (`--room-chip-1..10`, defined
 * in src/app.css via `light-dark()`) rather than raw hex, so the calendar
 * chips adapt to dark mode instead of hardcoding the light-mode palette.
 */

export const ROOM_COLOR_PALETTE: readonly string[] = [
  'var(--room-chip-1)', // blue
  'var(--room-chip-2)', // green
  'var(--room-chip-3)', // red
  'var(--room-chip-4)', // orange
  'var(--room-chip-5)', // purple
  'var(--room-chip-6)', // cyan
  'var(--room-chip-7)', // yellow
  'var(--room-chip-8)', // pink
  'var(--room-chip-9)', // lime
  'var(--room-chip-10)', // slate
] as const

export function roomColorByIndex(index: number): string {
  const n = ROOM_COLOR_PALETTE.length
  const i = ((index % n) + n) % n
  // Palette is non-empty and `i` is in [0, n), so this is always defined.
  return ROOM_COLOR_PALETTE[i] as string
}

/**
 * Derive an acronym from a room name.
 * - Split on whitespace, take first letter of each word.
 * - Uppercase.
 * - Max 3 characters.
 * - Falls back to `?` for empty input.
 */
export function roomAcronym(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  const words = trimmed.split(/\s+/)
  const letters = words.map((w) => w[0]?.toUpperCase() ?? '').join('')
  return letters.slice(0, 3) || '?'
}
